import React from "react";
import { fonts } from "../../../config";
import { masks, validations } from "../../../utils";
import { StylesForm } from "../styles";
import Grid from "@mui/material/GridLegacy";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  ClearButton,
  SubmitButton,
  InputText,
  InputSelect,
  InputDatePicker,
  InputPhone,
} from "../../../components/newComponents";

interface ConfigFieldsType {
  ACCESS_APPROVAL_ENABLED: boolean;
  CHECK_IN_DEFAULT_HOUR: number;
  CHECK_IN_DEFAULT_MINUTE: number;
  CHECK_OUT_DEFAULT_HOUR: number;
  CHECK_OUT_DEFAULT_MINUTE: number;
  FACIAL_FORMAT: string;
  GUEST_SHEET_BIRTHDATE_ENABLED: boolean;
  GUEST_SHEET_DOCUMENT_ENABLED: boolean;
  GUEST_SHEET_EMAIL_ENABLED: boolean;
  GUEST_SHEET_FACIAL_ENABLED: boolean;
  GUEST_SHEET_FACIAL_URL_ENABLED: boolean;
  GUEST_SHEET_NAME_ENABLED: boolean;
  GUEST_SHEET_PHONE_ENABLED: boolean;
  GUEST_SHEET_RENTER_PASSWORD_ENABLED: boolean;
  GUEST_SHEET_RESERVATION_PASSWORD_ENABLED: boolean;
  GUEST_SHEET_VEHICLE_PLATE_ENABLED: boolean;
  LEFT_IMAGE_CROP_PIXELS: number;
  LOWER_IMAGE_CROP_PIXELS: number;
  MASK_TYPE_FACIAL: string;
  PRE_ACCESS_APPROVAL_ENABLED: boolean;
  RIGHT_IMAGE_CROP_PIXELS: number;
  TOP_IMAGE_CROP_PIXELS: number;
}

const schema = z
  .object({
    fullName: z
      .string()
      .min(1, "Campo Obrigatório")
      .regex(
        /^[A-zÀ-Ÿ][A-zÀ-ÿ']+\s([A-zÀ-ÿ']\s?)*[A-zÀ-Ÿ][A-zÀ-ÿ']+$/,
        "Nome inválido (ex: João Silva)"
      ),
    docType: z.number(),
    docValue: z.string().default(""),
    birthday: z
      .date({ error: "Campo Obrigatório" })
      .max(new Date(), "Data inválida"),
    email: z.string().min(1, "Campo Obrigatório").email("E-mail inválido"),
    phone: z.string().optional(),
    vehiclePlate: z.string().optional(),
    number: z.number({ error: "Campo Obrigatório" }).min(0).max(10),
  })
  .superRefine((data, ctx) => {
    if (data.docType === 1) {
      const err = validations.validateCPF(data.docValue || "");
      if (err !== null) {
        ctx.addIssue({
          code: "custom",
          message: err || "CPF inválido",
          path: ["docValue"],
        });
      }
    }
  });

type FormValues = z.infer<typeof schema>;

function ReservationFormAdmin({ data, onSubmit, initialValues }: { data: any; onSubmit: (v: any) => void; initialValues?: any }) {
  const [totalMembers, setTotalMembers] = React.useState(data?.numGuests);
  const [formConfigFields, setFormConfigFields] = React.useState<
    Partial<ConfigFieldsType>
  >({});
  const [resetKey, setResetKey] = React.useState(0);

  const { control, handleSubmit, watch, reset, setValue, trigger } =
    useForm<FormValues>({
      resolver: zodResolver(schema) as any,
      defaultValues: {
        fullName: initialValues?.fullName ?? data?.primaryGuest?.name ?? "",
        docType: initialValues?.docType ?? (data?.primaryGuest?.document ? 1 : data?.primaryGuest?.passport ? 2 : 1),
        docValue: initialValues?.docValue ?? data?.primaryGuest?.document ?? data?.primaryGuest?.passport ?? "",
        birthday: initialValues?.birthday ?? (data?.primaryGuest?.birthDate ? new Date(data.primaryGuest.birthDate) : undefined),
        email: initialValues?.email ?? data?.primaryGuest?.email ?? "",
        phone: initialValues?.phone ?? data?.primaryGuest?.phone ?? "",
        vehiclePlate: initialValues?.vehiclePlate ?? data?.primaryGuest?.vehiclePlate ?? "",
        number: initialValues?.number ?? data?.numGuests ?? 0,
      },
    });

  const watchedDocType = watch("docType");

  const checkValueType = (value: string) => {
    if (value === "true" || value === "false") {
      return value === "true";
    } else {
      return parseFloat(value);
    }
  };

  const showInputs = () => {
    const formConfigData = data?.place?.configurationParameters?.map(
      (item) => [item?.type, checkValueType(item?.value)]
    );
    const fieldConfig = Object.fromEntries(formConfigData);
    setFormConfigFields(fieldConfig);
  };

  React.useEffect(() => {
    showInputs();
  }, [data]);

  const onSubmitHandler = (formValues: FormValues) => {
    onSubmit(formValues);
  };

  const handleReset = () => {
    reset({
      fullName: "",
      docType: 1,
      docValue: "",
      birthday: undefined,
      email: "",
      phone: "",
      vehiclePlate: "",
      number: 0,
    });
    setResetKey((k) => k + 1);
  };

  return (
    <StylesForm.HeaderSteps>
      <form onSubmit={handleSubmit(onSubmitHandler)} style={{ width: "100%" }}>
        <Grid alignItems="flex-end" container spacing={1}>

          <Grid item xs={6} md={12} lg={12}>
            <Controller
              name="fullName"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label="NOME COMPLETO"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              name="docType"
              control={control}
              render={({ field, fieldState }) => (
                <InputSelect
                  label="TIPO DO DOCUMENTO"
                  error={fieldState.error?.message}
                  value={field.value}
                  options={[
                    { value: 1, label: "CPF" },
                    { value: 2, label: "Passaporte" },
                  ]}
                  onChange={(value) => {
                    field.onChange(Number(value));
                    setValue("docValue", "");
                    trigger("docValue");
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              name="docValue"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label={watchedDocType === 1 ? "CPF" : "PASSAPORTE"}
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={(e) => {
                    const value =
                      watchedDocType === 1
                        ? masks.CPF(e.target.value)
                        : e.target.value;
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              key={resetKey}
              name="birthday"
              control={control}
              render={({ field, fieldState }) => (
                <InputDatePicker
                  label="DATA NASCIMENTO"
                  error={fieldState.error?.message}
                  value={field.value ?? null}
                  onChange={(date) => field.onChange(date ?? undefined)}
                  onBlur={field.onBlur}
                  maxDate={new Date()}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label="E-MAIL"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <InputPhone
                  label="Telefone"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  placeholder="TELEFONE"
                />
              )}
            />
          </Grid>

          {formConfigFields.GUEST_SHEET_VEHICLE_PLATE_ENABLED && (
            <Grid item xs={6} md={6} lg={6}>
              <Controller
                name="vehiclePlate"
                control={control}
                render={({ field, fieldState }) => (
                  <InputText
                    label="Placa do Veículo"
                    error={fieldState.error?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item xs={6} md={6} lg={6}>
            <Controller
              name="number"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  label="Nº DE HÓSPEDES ACOMPANHANTES"
                  type="number"
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={(e) => {
                    const val = Number(
                      masks.onlyNumbers(e.target.value, 0, 10)
                    );
                    field.onChange(val);
                    setTotalMembers(val);
                  }}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              )}
            />
          </Grid>
        </Grid>

        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "8px",
            marginBottom: "16px",
            marginTop: "16px",
          }}
        >
          <ClearButton
            fullWidth={false}
            variant="text"
            type="button"
            onClick={handleReset}
          >
            Limpar Campos
          </ClearButton>
          <SubmitButton fullWidth={false} type="submit">
            Confirmar
          </SubmitButton>
        </div>
      </form>
    </StylesForm.HeaderSteps>
  );
}

export default ReservationFormAdmin;
